import {
  Bell,
  BookOpen,
  Briefcase,
  CalendarDays,
  Camera,
  ClipboardCheck,
  ClipboardList,
  FileText,
  FolderKanban,
  LifeBuoy,
  MapPinned,
  NotebookPen,
  Settings,
  Shield,
  SlidersHorizontal,
  Truck,
  Users,
} from "lucide-react";
import { TENANT_GOVERNANCE_PERMISSION_GROUPS } from "../constants/tenantGovernancePermissions";
import {
  canAccessTenantAdmin,
  getRoleNames,
  hasAnyPermission,
} from "../utils/tenantAdminAccess";

const WORKSPACE_GROUPS = [
  {
    key: "start",
    label: "Start",
    description: "Overzicht, inbox en directe cockpitingangen.",
    icon: BookOpen,
  },
  {
    key: "planning",
    label: "Planning",
    description: "Planning, inzet en teamafstemming.",
    icon: CalendarDays,
  },
  {
    key: "werk",
    label: "Werk",
    description: "Werkbonnen, uitvoering en operationele input.",
    icon: ClipboardList,
  },
  {
    key: "projecten",
    label: "Projecten",
    description: "Projectregie, calculatie en voortgang.",
    icon: FolderKanban,
  },
  {
    key: "klanten",
    label: "Klanten",
    description: "Relaties en klantdossiers.",
    icon: Users,
  },
  {
    key: "uren",
    label: "Uren",
    description: "Urenregistratie, soorten en mandagstaten.",
    icon: CalendarDays,
  },
  {
    key: "financieel",
    label: "Financieel",
    description: "Inkoop, factuurcontrole en financiële signalen.",
    icon: Briefcase,
  },
  {
    key: "borging",
    label: "Borging",
    description: "KAM, meldingen en kwaliteitsborging.",
    icon: Shield,
  },
  {
    key: "beheer",
    label: "Beheer",
    description: "Instellingen, tenantinrichting en support.",
    icon: Settings,
  },
];

const MONTEUR_SECTION_DEFS = [
  { key: "werk", title: "WERK" },
  { key: "info", title: "INFO" },
  { key: "instellingen", title: "INSTELLINGEN" },
];

const NAVIGATION_ITEMS = [
  {
    key: "pl-dashboard",
    group: "start",
    label: "Dashboard",
    description: "Snel overzicht van de belangrijkste stuurinformatie.",
    to: "/pl",
    icon: BookOpen,
    surfaces: ["workspace"],
    visible: () => true,
    matchExact: true,
  },
  {
    key: "pl-meldingen",
    group: "start",
    label: "Meldingen",
    description: "Openstaande berichten en attenties.",
    to: "/pl/meldingen",
    icon: Bell,
    surfaces: ["workspace"],
    visible: () => true,
    badge: (context) => toBadge(context?.workspaceCounters?.notificationsUnread),
  },
  {
    key: "pl-planning",
    group: "planning",
    label: "Planning",
    description: "Dagelijkse planning en nog niet geplande werkbonnen.",
    to: "/pl/planning",
    icon: CalendarDays,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
    badge: (context) => toBadge(context?.workspaceCounters?.planningOpen),
  },
  {
    key: "pl-inzet-capaciteit",
    group: "planning",
    label: "Inzet & capaciteit",
    description: "Capaciteit, bezetting en inzetruimte per team.",
    to: "/pl/inzet-capaciteit",
    icon: CalendarDays,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-projectweekoverzicht",
    group: "planning",
    label: "Projectweekoverzicht",
    description: "Weeksturing op projecten en uitvoering.",
    to: "/pl/projectweekoverzicht",
    icon: CalendarDays,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
  },
  {
    key: "pl-monteurs",
    group: "planning",
    label: "Monteurs",
    description: "Capaciteit en teambezetting per monteur.",
    to: "/pl/monteurs",
    icon: Users,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
  },
  {
    key: "pl-verlof",
    group: "planning",
    label: "Verlof",
    description: "Openstaande verlofaanvragen en planningseffecten.",
    to: "/pl/verlof",
    icon: CalendarDays,
    surfaces: ["workspace"],
    visible: () => true,
    badge: (context) => toBadge(context?.workspaceCounters?.leavePending),
  },
  {
    key: "pl-werkbon-nieuw",
    group: "werk",
    label: "Nieuwe werkbon",
    description: "Start direct een nieuwe werkbon.",
    to: "/werkbon/nieuw",
    icon: Briefcase,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
    matchExact: true,
  },
  {
    key: "pl-werkbonnen",
    group: "werk",
    label: "Werkbonnen",
    description: "Overzicht van alle werkbonnen en statussen.",
    to: "/werkbon",
    icon: ClipboardList,
    surfaces: ["workspace"],
    visible: () => true,
    badge: (context) => toBadge(context?.workspaceCounters?.workordersReady),
  },
  {
    key: "pl-opnames",
    group: "werk",
    label: "Opnames",
    description: "Opnamewerkbonnen en intake-invoer.",
    to: "/opname",
    icon: ClipboardList,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-fotos",
    group: "werk",
    label: "Foto-overzicht",
    description: "Centraal overzicht van project- en werkbonfoto's.",
    to: "/pl/fotos",
    icon: Camera,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-projecten",
    group: "projecten",
    label: "Projecten",
    description: "Projectlijst en dossierinstroom.",
    to: "/pl/projecten",
    icon: FolderKanban,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-assets",
    group: "projecten",
    label: "Technische assets",
    description: "Assets, verbindingen en React Flow-diagrammen per klant en locatie.",
    to: "/pl/assets",
    icon: MapPinned,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
  },
  {
    key: "pl-projectcontrol",
    group: "projecten",
    label: "Projectcontrol",
    description: "Bewaking op voortgang, risico en bijsturing.",
    to: "/pl/projectcontrol",
    icon: Briefcase,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-calculaties",
    group: "projecten",
    label: "Calculaties",
    description: "Calculaties en kostprijsafstemming.",
    to: "/pl/calculaties",
    icon: Briefcase,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
  },
  {
    key: "pl-installaties",
    group: "projecten",
    label: "Installaties",
    description: "Brandveiligheidsinstallaties en logboekroutes.",
    to: "/pl/installaties",
    icon: Briefcase,
    surfaces: ["workspace"],
    visible: (context) =>
      Boolean(context.fireSafetyModuleEnabled) && !context.isControllerOnly,
    activeAliases: ["/pl/brandveiligheid", "/pl/brandveiligheid/installaties"],
  },
  {
    key: "pl-klanten",
    group: "klanten",
    label: "Klanten",
    description: "Klantdossiers, import en relatiebeheer.",
    to: "/klanten",
    icon: Users,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-urenbeheer",
    group: "uren",
    label: "Urenbeheer",
    description: "Controle op urenregels, statussen en locks.",
    to: "/pl/uren",
    icon: CalendarDays,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
  },
  {
    key: "pl-uursoorten",
    group: "uren",
    label: "Uursoorten",
    description: "Beheer van uursoorten en categorieen.",
    to: "/pl/uursoorten",
    icon: Briefcase,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
  },
  {
    key: "pl-mandagstaten",
    group: "uren",
    label: "Mandagstaten",
    description: "Status en verwerking van mandagstaten.",
    to: "/pl/mandagstaten",
    icon: ClipboardList,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
  },
  {
    key: "pl-inkoop",
    group: "financieel",
    label: "Inkoop",
    description: "Openstaande inkooporders en leveranciersacties.",
    to: "/pl/inkoop",
    icon: Briefcase,
    surfaces: ["workspace"],
    visible: () => true,
    badge: (context) => toBadge(context?.workspaceCounters?.procurementOpen),
  },
  {
    key: "pl-inkoop-verwerking",
    group: "financieel",
    label: "Inkoop verwerking",
    description: "Verwerking en ontvangst van inkooporders.",
    to: "/pl/inkoop/verwerking",
    icon: Briefcase,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-factuurcontrole",
    group: "financieel",
    label: "Factuurcontrole",
    description: "Controle van binnenkomende leveranciersfacturen.",
    to: "/pl/inkoop/facturen",
    icon: Briefcase,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-externe-inzet",
    group: "financieel",
    label: "Externe inzet",
    description: "Externe capaciteit, status en opvolging.",
    to: "/pl/externe-inzet",
    icon: Truck,
    surfaces: ["workspace"],
    visible: () => true,
    badge: (context) => toBadge(context?.workspaceCounters?.externalDeploymentOpen),
  },
  {
    key: "pl-finance-safety",
    group: "financieel",
    label: "Risico's",
    description: "Financiele controle- en risicosignalen.",
    to: "/pl/finance-safety",
    icon: Briefcase,
    surfaces: ["workspace"],
    visible: (context) => context.canFinancialControlView,
  },
  {
    key: "pl-kam-dashboard",
    group: "borging",
    label: "Dashboard",
    description: "KAM-overzicht en kwaliteitsindicatoren.",
    to: "/pl/kam",
    icon: ClipboardList,
    surfaces: ["workspace"],
    visible: (context) => context.canKamView,
  },
  {
    key: "pl-kam-meldingen",
    group: "borging",
    label: "Meldingen",
    description: "Veiligheids- en kwaliteitsmeldingen.",
    to: "/pl/kam/meldingen",
    icon: Bell,
    surfaces: ["workspace"],
    visible: (context) => context.canKamView || context.canKamReportCreate,
  },
  {
    key: "pl-kam-toolboxen",
    group: "borging",
    label: "Toolboxen",
    description: "Toolboxen en veiligheidsinstructies.",
    to: "/pl/kam/toolboxen",
    icon: ClipboardList,
    surfaces: ["workspace"],
    visible: (context) => context.canKamToolboxView,
  },
  {
    key: "pl-kam-analyse",
    group: "borging",
    label: "Analyse",
    description: "Analyse op KAM-signalen en trends.",
    to: "/pl/kam/analyse",
    icon: BookOpen,
    surfaces: ["workspace"],
    visible: (context) => context.canKamView,
  },
  {
    key: "pl-weergave",
    group: "beheer",
    label: "Weergave",
    description: "Persoonlijke display- en navigatievoorkeuren.",
    to: "/pl/weergave",
    icon: Settings,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-kennisbank",
    group: "beheer",
    label: "Kennisbank",
    description: "Handleidingen, afspraken en naslag.",
    to: "/pl/kennisbank",
    icon: BookOpen,
    surfaces: ["workspace"],
    visible: () => true,
  },
  {
    key: "pl-onboarding",
    group: "beheer",
    label: "Onboarding",
    description: "Tenant-onboarding en basisinrichting.",
    to: "/pl/onboarding",
    icon: Settings,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
  },
  {
    key: "tenant-admin-hub",
    group: "beheer",
    label: "Bedrijfsinrichting",
    description: "Tenant-inrichting en governancehub.",
    to: "/tenant-admin",
    icon: Settings,
    surfaces: ["workspace"],
    visible: (context) =>
      !context.isControllerOnly &&
      context.canTenantAdminHubAccess &&
      context.tenantLayerVisible,
  },
  {
    key: "tenant-admin-users",
    group: "beheer",
    label: "Gebruikers",
    description: "Gebruikersbeheer binnen de tenant.",
    to: "/tenant-admin/users",
    icon: Users,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly && context.canTenantAdminUsersAccess,
  },
  {
    key: "tenant-admin-roles",
    group: "beheer",
    label: "Rollen & rechten",
    description: "RBAC en roltoewijzingen beheren.",
    to: "/tenant-admin/roles",
    icon: Settings,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly && context.canTenantAdminRolesAccess,
  },
  {
    key: "tenant-admin-settings",
    group: "beheer",
    label: "Bedrijfsinstellingen",
    description: "Instellingen en tenantregels.",
    to: "/tenant-admin/settings",
    icon: Settings,
    surfaces: ["workspace"],
    visible: (context) =>
      !context.isControllerOnly && context.canTenantAdminSettingsAccess,
  },
  {
    key: "tenant-admin-branding",
    group: "beheer",
    label: "Huisstijl & documenten",
    description: "Branding, documenten en templates.",
    to: "/tenant-admin/bedrijfsinrichting/huisstijl-documenten",
    icon: Settings,
    surfaces: ["workspace"],
    visible: (context) =>
      !context.isControllerOnly &&
      context.canTenantAdminBrandingDocumentsAccess,
  },
  {
    key: "tenant-admin-status",
    group: "beheer",
    label: "Inrichtingsstatus",
    description: "Status van inrichting, modules en voortgang.",
    to: "/tenant-admin/status",
    icon: Settings,
    surfaces: ["workspace"],
    visible: (context) =>
      !context.isControllerOnly && context.canTenantAdminSettingsAccess,
  },
  {
    key: "pl-support-tickets",
    group: "beheer",
    label: "Support tickets",
    description: "Tenant-support en operationele vragen.",
    to: "/pl/support-tickets",
    icon: LifeBuoy,
    surfaces: ["workspace"],
    visible: (context) => !context.isControllerOnly,
  },
  {
    key: "monteur-open-workorders",
    group: "planning",
    label: "Openstaande bonnen",
    description: "Werkbonnen die nog uitvoering vragen.",
    to: "/monteur/werkbonnen?status=open",
    icon: Briefcase,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    matchPrefixes: ["/monteur/werkbonnen"],
    matchQuery: { status: "open" },
    badge: (context) => toBadge(context?.monteurCounters?.workordersOpen),
    monteurSection: "werk",
    legacyMonteurGroupKey: "werk",
    legacyMonteurItemKey: "werkbonnen",
  },
  {
    key: "monteur-done-workorders",
    group: "werk",
    label: "Afgeronde bonnen",
    description: "Afgeronde of gesloten werkbonnen.",
    to: "/monteur/werkbonnen?status=done",
    icon: ClipboardCheck,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    matchPrefixes: ["/monteur/werkbonnen"],
    matchQuery: { status: "done" },
    badge: (context) => toBadge(context?.monteurCounters?.workordersDone),
    monteurSection: "werk",
    legacyMonteurGroupKey: "werk",
    legacyMonteurItemKey: "werkbonnenAfgerond",
  },
  {
    key: "monteur-new-workorder",
    group: "werk",
    label: "Nieuwe werkbon",
    description: "Start direct een nieuwe werkbon.",
    to: "/monteur/werkbon/nieuw",
    icon: Briefcase,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    matchExact: true,
    monteurSection: "werk",
    legacyMonteurGroupKey: "werk",
    legacyMonteurItemKey: "werkbonNieuw",
  },
  {
    key: "monteur-opnames",
    group: "werk",
    label: "Opnames",
    description: "Uit te voeren opnames en registratie.",
    to: "/monteur/opnames",
    icon: ClipboardCheck,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    badge: (context) => toBadge(context?.monteurCounters?.opnamesTotal),
    monteurSection: "werk",
    legacyMonteurGroupKey: "werk",
    legacyMonteurItemKey: "opnames",
  },
  {
    key: "monteur-site-survey",
    group: "werk",
    label: "Site Survey",
    description: "Locatie-opname en voorbereidende checks.",
    to: "/monteur/site-survey",
    icon: MapPinned,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    monteurSection: "werk",
    legacyMonteurGroupKey: "werk",
    legacyMonteurItemKey: "siteSurvey",
  },
  {
    key: "monteur-meldingen",
    group: "start",
    label: "Meldingen",
    description: "Persoonlijke meldingen en updates.",
    to: "/monteur/meldingen",
    icon: Bell,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    badge: (context) => toBadge(context?.monteurCounters?.notificationsUnread),
    monteurSection: "info",
    legacyMonteurGroupKey: "info",
    legacyMonteurItemKey: "meldingen",
  },
  {
    key: "monteur-fotos",
    group: "werk",
    label: "Foto's",
    description: "Foto-overzicht en uploadstatus.",
    to: "/monteur/fotos",
    icon: Camera,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    monteurSection: "info",
    legacyMonteurGroupKey: "info",
    legacyMonteurItemKey: "fotos",
  },
  {
    key: "monteur-formulieren",
    group: "werk",
    label: "Formulieren",
    description: "Beschikbare formulieren en templates.",
    to: "/monteur/templates",
    icon: ClipboardList,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    monteurSection: "info",
    legacyMonteurGroupKey: "info",
    legacyMonteurItemKey: "formulieren",
  },
  {
    key: "monteur-notities",
    group: "werk",
    label: "Notities",
    description: "Persoonlijke notities en overdracht.",
    to: "/monteur/notities",
    icon: NotebookPen,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    monteurSection: "info",
    legacyMonteurGroupKey: "info",
    legacyMonteurItemKey: "notities",
  },
  {
    key: "monteur-toolbox",
    group: "borging",
    label: "Toolbox",
    description: "Toolboxitems en veiligheidsinstructies.",
    to: "/monteur/toolbox",
    icon: Shield,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    monteurSection: "info",
    legacyMonteurGroupKey: "info",
    legacyMonteurItemKey: "toolbox",
  },
  {
    key: "monteur-documenten",
    group: "beheer",
    label: "Mijn documenten",
    description: "Persoonlijke documenten en downloads.",
    to: "/monteur/documenten",
    icon: FileText,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    monteurSection: "info",
    legacyMonteurGroupKey: "info",
    legacyMonteurItemKey: "documenten",
  },
  {
    key: "monteur-verlof",
    group: "uren",
    label: "Verlof",
    description: "Eigen verlofstatus en aanvragen.",
    to: "/monteur/verlof",
    icon: CalendarDays,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    badge: (context) => toBadge(context?.monteurCounters?.leavePending),
    monteurSection: "instellingen",
    legacyMonteurGroupKey: "instellingen",
    legacyMonteurItemKey: "verlof",
  },
  {
    key: "monteur-weergave",
    group: "beheer",
    label: "Weergave",
    description: "Persoonlijke voorkeuren en weergave-opties.",
    to: "/monteur/weergave",
    icon: SlidersHorizontal,
    surfaces: ["monteurSidebar"],
    visible: (context) => context.roleNames.has("monteur"),
    monteurSection: "instellingen",
    legacyMonteurGroupKey: "instellingen",
    legacyMonteurItemKey: "weergave",
  },
];

export function buildWorkspaceNavigation(input = {}) {
  const context = createNavigationContext(input);

  return WORKSPACE_GROUPS.map((group) => {
    const items = NAVIGATION_ITEMS.filter(
      (item) =>
        item.group === group.key &&
        item.surfaces.includes("workspace") &&
        isVisible(item, context)
    ).map((item) => decorateItem(item, context));

    return {
      ...group,
      items,
    };
  }).filter((group) => group.items.length > 0);
}

export function buildMonteurSidebarSections(input = {}) {
  const context = createNavigationContext(input);

  return MONTEUR_SECTION_DEFS.map((section) => {
    const items = NAVIGATION_ITEMS.filter(
      (item) =>
        item.surfaces.includes("monteurSidebar") &&
        item.monteurSection === section.key &&
        isVisible(item, context) &&
        isEnabledInMonteurConfig(item, context.monteurMenuConfig)
    ).map((item) => decorateItem(item, context));

    return {
      key: section.key,
      title: section.title,
      items,
    };
  }).filter((section) => section.items.length > 0);
}

export function createNavigationContext(input = {}) {
  const user = input.user || null;
  const roleNames = new Set(getRoleNames(user));
  const permissionSet = new Set(
    (Array.isArray(user?.permissions) ? user.permissions : [])
      .map((permission) => String(permission || "").trim().toLowerCase())
      .filter(Boolean)
  );

  const canKamView = permissionSet.has("kam.view");
  const canKamReportCreate = permissionSet.has("kam.report.create");
  const canKamToolboxView = permissionSet.has("kam.toolbox.view");
  const canFinancialControlView =
    permissionSet.has("financial_control.risks.view") ||
    permissionSet.has("financial_control.view") ||
    permissionSet.has("financial_control.manage");
  const canTenantAdminHubAccess = canAccessTenantAdmin(user);
  const hasFullTenantAdminRole =
    roleNames.has("tenantadmin") || roleNames.has("platformadmin");
  const canTenantAdminUsersAccess =
    canTenantAdminHubAccess &&
    (hasFullTenantAdminRole ||
      hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.usersManage));
  const canTenantAdminRolesAccess =
    canTenantAdminHubAccess &&
    (hasFullTenantAdminRole ||
      hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.rolesManage));
  const canTenantAdminSettingsAccess =
    canTenantAdminHubAccess &&
    (hasFullTenantAdminRole ||
      hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.settingsView));
  const canTenantAdminBrandingDocumentsAccess =
    canTenantAdminHubAccess &&
    (hasFullTenantAdminRole ||
      hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.companyDetailsView));

  const isControllerOnly =
    roleNames.has("controller") &&
    !roleNames.has("projectleider") &&
    !roleNames.has("tenantadmin") &&
    !roleNames.has("platformadmin") &&
    !roleNames.has("zzp_owner");

  return {
    user,
    roleNames,
    permissionSet,
    workspaceCounters: input.workspaceCounters || {},
    monteurCounters: input.monteurCounters || {},
    fireSafetyModuleEnabled: Boolean(input.fireSafetyModuleEnabled),
    tenantLayerVisible: input.tenantLayerVisible !== false,
    canKamView,
    canKamReportCreate,
    canKamToolboxView,
    canFinancialControlView,
    canTenantAdminHubAccess,
    canTenantAdminUsersAccess,
    canTenantAdminRolesAccess,
    canTenantAdminSettingsAccess,
    canTenantAdminBrandingDocumentsAccess,
    isControllerOnly,
    monteurMenuConfig: input.monteurMenuConfig || null,
  };
}

export function resolveActiveNavigationItem(items, locationLike) {
  const list = Array.isArray(items) ? items : [];
  let bestItem = null;
  let bestScore = -1;

  for (const item of list) {
    const score = getNavigationItemMatchScore(item, locationLike);
    if (score > bestScore) {
      bestItem = item;
      bestScore = score;
    }
  }

  return {
    item: bestItem,
    to: String(bestItem?.to || ""),
    score: bestScore,
  };
}

export function isNavigationItemActive(item, locationLike) {
  return getNavigationItemMatchScore(item, locationLike) >= 0;
}

function decorateItem(item, context) {
  return {
    ...item,
    badge: typeof item.badge === "function" ? item.badge(context) : item.badge,
  };
}

function isVisible(item, context) {
  if (typeof item.visible !== "function") return true;
  return item.visible(context) !== false;
}

function isEnabledInMonteurConfig(item, monteurMenuConfig) {
  const groupKey = String(item?.legacyMonteurGroupKey || "").trim();
  const itemKey = String(item?.legacyMonteurItemKey || "").trim();
  if (!groupKey || !itemKey) return true;

  const groupConfig = monteurMenuConfig?.[groupKey];
  if (groupConfig?.enabled === false) return false;
  if (groupConfig?.items?.[itemKey] === false) return false;
  return true;
}

function getNavigationItemMatchScore(item, locationLike) {
  if (!item) return -1;

  const pathname = normalizePath(
    typeof locationLike === "string" ? locationLike : locationLike?.pathname
  );
  const search =
    typeof locationLike === "object" && locationLike !== null
      ? String(locationLike.search || "")
      : "";
  if (!pathname) return -1;

  const targetPath = getPathFromTo(item.to);
  const rawCandidates = [
    targetPath,
    ...(Array.isArray(item.activeAliases) ? item.activeAliases : []),
  ];
  const rawPrefixes =
    Array.isArray(item.matchPrefixes) && item.matchPrefixes.length > 0
      ? item.matchPrefixes
      : rawCandidates;
  const candidates = rawPrefixes.map((value) => getPathFromTo(value)).filter(Boolean);

  let bestScore = -1;
  for (const candidate of candidates) {
    const score = item.matchExact
      ? getExactMatchScore(candidate, pathname)
      : getPrefixMatchScore(candidate, pathname);
    if (score > bestScore) bestScore = score;
  }

  if (bestScore < 0) return -1;
  if (!matchesQuery(item.matchQuery, search)) return -1;
  return bestScore;
}

function matchesQuery(matchQuery, search) {
  if (!matchQuery || typeof matchQuery !== "object") return true;
  const params = new URLSearchParams(search);

  for (const [key, expectedRaw] of Object.entries(matchQuery)) {
    const expected = String(expectedRaw || "").trim().toLowerCase();
    const current = String(params.get(key) || "").trim().toLowerCase();

    if (!current) {
      if (expected === "open") continue;
      return false;
    }
    if (current !== expected) return false;
  }

  return true;
}

function getExactMatchScore(target, current) {
  const normalizedTarget = normalizePath(target);
  const normalizedCurrent = normalizePath(current);
  if (!normalizedTarget || !normalizedCurrent) return -1;
  return normalizedTarget === normalizedCurrent ? normalizedTarget.length + 10000 : -1;
}

function getPrefixMatchScore(target, current) {
  const normalizedTarget = normalizePath(target);
  const normalizedCurrent = normalizePath(current);
  if (!normalizedTarget || !normalizedCurrent) return -1;
  if (normalizedTarget === "/pl") {
    return normalizedCurrent === "/pl" ? 10000 : -1;
  }
  if (normalizedCurrent === normalizedTarget) return normalizedTarget.length + 10000;
  if (normalizedCurrent.startsWith(`${normalizedTarget}/`)) return normalizedTarget.length;
  return -1;
}

function getPathFromTo(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const [path] = raw.split("?");
  return normalizePath(path);
}

function normalizePath(value) {
  const raw = String(value || "")
    .trim()
    .replace(/[?#].*$/, "");
  if (!raw) return "";
  return raw.length > 1 ? raw.replace(/\/+$/, "") : raw;
}

function toBadge(value) {
  const count = Number(value || 0);
  if (!Number.isFinite(count) || count <= 0) return undefined;
  return String(count);
}
